import { useEffect, useState } from "react";
import { Play, CheckCircle2, RefreshCw, Download, ChevronLeft, ChevronRight, AlertCircle, Loader2, Coins, Undo2, Trash2, CopyPlus, Scissors, Clapperboard } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, fileUrl, API, sceneFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "./StatusBadge";
import { FilmStrip } from "./FilmStrip";
import { FullEpisode } from "./FullEpisode";
import { TakesStrip } from "./TakesStrip";
import { TrimPanel } from "./TrimPanel";
import { EpisodePreview } from "./EpisodePreview";
import { undoToast } from "./RecentlyDeleted";

const Player = ({ s, aspect }) => (
  <div className={`relative bg-black rounded-lg overflow-hidden border border-[#2A3245] mx-auto ${aspect === "9:16" ? "aspect-[9/16] max-h-[60vh]" : "aspect-video w-full max-w-[calc(62vh*16/9)]"}`} data-testid="preview-player">
    {s?.video_path ? (
      <video key={s.video_path} data-testid="preview-video" src={fileUrl(s.video_path)} poster={fileUrl(s.thumb_path)} controls playsInline className="w-full h-full object-contain" />
    ) : (
      <div className={`absolute inset-0 grid place-items-center text-center px-6 ${s?.status === "generating" ? "scan" : ""}`}>
        {s?.status === "generating" ? (
          <div className="text-violet-300 space-y-2" data-testid="generating-indicator">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
            <div className="font-mono text-xs">{s.progress || "Generating"}… (usually 3–10 min)</div>
          </div>
        ) : <div className="text-slate-600 text-sm"><Play className="w-8 h-8 mx-auto mb-2" />No clip yet for this scene</div>}
      </div>
    )}
    {s?.video_path && s.status === "generating" && (
      <div className="absolute top-2 left-2 font-mono text-[10px] bg-violet-600/80 px-2 py-1 rounded" data-testid="regenerating-indicator">
        New take: {s.progress || "generating"}…
      </div>
    )}
  </div>
);

const Controls = ({ s, prev, ep, reload }) => {
  const [tweak, setTweak] = useState(s.tweak || "");
  useEffect(() => setTweak(s.tweak || ""), [s.id]); // eslint-disable-line
  const notLocked = (s.cast_found || []).filter((c) => !c.locked).map((c) => c.name);
  const unmatched = s.cast_unmatched || [];
  const gen = async (keep) => {
    const msg = `${keep ? "Keep the current take and film another" : s.video_path ? "Replace the current take with a new one" : "Generate"} for scene ${s.number}?${keep && s.status === "approved" ? " The scene goes back to 'ready' when the new take lands — then approve the take you prefer." : ""} Estimated cost ≈ ${ep.est_credits_per_scene} credits from your Universal Key balance.`;
    if (!window.confirm(msg)) return;
    try { await http.post(`/scenes/${s.id}/generate`, { tweak, keep_current: keep }); toast(keep ? "Filming another take — the current one is kept." : "Filming started — two 10s shots, then joined."); await reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async (un) => { try { await http.post(`/scenes/${s.id}/${un ? "unapprove" : "approve"}`); await reload(); } catch (e) { toast.error(errMsg(e)); } };
  const remove = async () => {
    if (!window.confirm(`Delete scene ${s.number}${s.status === "approved" ? " and its approved clip" : ""}? Later scenes are renumbered. You can undo for 10 minutes.`)) return;
    try { const { data } = await http.delete(`/scenes/${s.id}`); undoToast(ep.id, data.batch_id, `Scene ${s.number}`, reload); await reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const busy = s.status === "generating";

  return (
    <div className="panel p-5 space-y-4" data-testid="scene-controls">
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm text-amber-400" data-testid="active-scene-number">SCENE {String(s.number).padStart(3, "0")}</span>
        <StatusBadge status={s.status} testId="active-scene-status" />
        {s.takes > 0 && <span className="font-mono text-[10px] text-slate-500">TAKE {s.takes}</span>}
        <button data-testid="studio-delete-scene-btn" title="Delete this scene" disabled={busy} onClick={remove}
          className="ml-auto p-1.5 rounded text-slate-500 hover:text-red-400 hover:bg-[#212738] disabled:opacity-30 transition-colors"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div>
        <div className="text-sm font-medium">{s.heading}</div>
        <p className="text-xs text-slate-400 mt-1 leading-relaxed">{s.action}</p>
      </div>
      <div className="space-y-1.5 max-h-40 overflow-y-auto">
        {s.dialogue.map((d, i) => (
          <div key={i} className="text-xs"><span className="font-mono text-cyan-400 uppercase mr-2">{d.speaker}</span><span className="text-slate-300">{d.line}</span></div>
        ))}
      </div>
      <div className="text-[11px] text-slate-500" data-testid="continuity-note">
        {s.number === 1 ? (ep.previous_title && ep.start_from_previous
            ? (ep.previous_has_frame ? `Opens from the last frame of the final approved scene of "${ep.previous_title}".` : `Will open from "${ep.previous_title}" once it has an approved scene.`)
            : "Opening scene — starts fresh from the cast references.")
          : prev?.status === "approved" ? `Continues from the last frame of scene ${prev.number}.`
          : `Tip: approve scene ${s.number - 1} first so this clip continues from its last frame.`}
      </div>
      {(s.cast_found || []).length > 0 && (
        <div className="text-[11px] text-slate-400" data-testid="scene-cast-used">
          Cast in this clip: {s.cast_found.map((c) => c.name).join(", ")}
          {s.dubbed?.length > 0 && <span data-testid="scene-dubbed-note" className="text-cyan-300"> · locked voice dubbed for {s.dubbed.join(", ")}</span>}
        </div>
      )}
      {s.dub_warning && <div data-testid="scene-dub-warning" className="text-[11px] text-amber-300">{s.dub_warning}</div>}
      {notLocked.length > 0 && (
        <div className="text-[11px] text-amber-300 flex gap-1.5" data-testid="unlocked-cast-warning">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />Look not locked yet: {notLocked.join(", ")}. Lock them in Cast to keep them consistent.
        </div>
      )}
      {unmatched.length > 0 && (
        <div className="text-[11px] text-amber-300 flex gap-1.5" data-testid="unmatched-cast-warning">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />No Cast character found for: {unmatched.join(", ")}. Use the exact Cast name in the scene, or add them in Cast.
        </div>
      )}
      {(s.status === "failed" || s.error?.startsWith("Trim failed")) && s.error && (
        <div className="text-xs text-red-400 flex gap-1.5" data-testid="scene-error"><AlertCircle className="w-3.5 h-3.5 shrink-0" />{s.error}</div>
      )}
      <Textarea data-testid="scene-tweak-input" rows={2} placeholder="Optional director's note for this take (e.g. 'she looks more surprised', 'warmer light')" value={tweak} onChange={(e) => setTweak(e.target.value)} />
      <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-mono" data-testid="cost-estimate"><Coins className="w-3.5 h-3.5 text-amber-400" />≈ {ep.est_credits_per_scene} credits per 20s scene (estimate)</div>
      <div className="flex flex-wrap gap-2">
        <Button data-testid="generate-scene-btn" disabled={busy} onClick={() => gen(false)} className="rounded-full flex-1">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {busy ? "Filming…" : s.video_path ? "Replace take" : "Generate clip"}
        </Button>
        {s.video_path && (
          <Button data-testid="keep-and-generate-btn" variant="secondary" disabled={busy} onClick={() => gen(true)} className="rounded-full flex-1">
            <CopyPlus className="w-4 h-4 mr-2" />Keep & try another
          </Button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {s.status === "approved" ? (
          <Button data-testid="unapprove-scene-btn" variant="secondary" onClick={() => approve(true)} className="rounded-full"><Undo2 className="w-4 h-4 mr-2" />Unapprove</Button>
        ) : (
          <Button data-testid="approve-scene-btn" variant="secondary" disabled={!s.video_path || busy} onClick={() => approve(false)} className="rounded-full flex-1 border border-amber-500/40 text-amber-300">
            <CheckCircle2 className="w-4 h-4 mr-2" />{(s.takes_list || []).length > 1 ? "Approve this take" : "Approve"}
          </Button>
        )}
        {s.video_path && (
          <a data-testid="download-clip-btn" href={fileUrl(s.video_path, sceneFile(s.number))} className="inline-flex items-center px-4 h-10 rounded-full bg-[#212738] text-sm hover:bg-[#2A3245] transition-colors">
            <Download className="w-4 h-4" />
          </a>
        )}
      </div>
    </div>
  );
};

export const StudioTab = ({ ep, scenes, chars, reload }) => {
  const [activeId, setActiveId] = useState(null);
  const [trimming, setTrimming] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  useEffect(() => setTrimming(false), [activeId]);
  useEffect(() => {
    if ((!activeId || !scenes.some((x) => x.id === activeId)) && scenes.length) setActiveId((scenes.find((s) => s.status !== "approved") || scenes[0]).id);
  }, [scenes, activeId]);
  const idx = scenes.findIndex((s) => s.id === activeId);
  const s = scenes[idx];
  const approved = scenes.filter((x) => x.status === "approved").length;
  if (!scenes.length) return <div className="panel p-8 text-sm text-slate-400" data-testid="studio-empty">Split your script into scenes first.</div>;

  return (
    <div className="space-y-5" data-testid="studio-tab">
      <div className="grid lg:grid-cols-[1fr_380px] gap-5">
        <div className="space-y-3">
          {trimming && s?.video_path ? <TrimPanel s={s} aspect={ep.aspect_ratio} onClose={() => setTrimming(false)} reload={reload} /> : <Player s={s} aspect={ep.aspect_ratio} />}
          {s && <TakesStrip s={s} reload={reload} />}
          <div className="flex items-center gap-2">
            <Button data-testid="prev-scene-btn" size="sm" variant="secondary" disabled={idx <= 0} onClick={() => setActiveId(scenes[idx - 1].id)} className="rounded-full"><ChevronLeft className="w-4 h-4" /></Button>
            <Button data-testid="next-scene-btn" size="sm" variant="secondary" disabled={idx >= scenes.length - 1} onClick={() => setActiveId(scenes[idx + 1].id)} className="rounded-full"><ChevronRight className="w-4 h-4" /></Button>
            <span className="font-mono text-xs text-slate-500">{idx + 1} / {scenes.length}</span>
            {s?.video_path && !trimming && (
              <Button data-testid="trim-clip-btn" size="sm" variant="secondary" disabled={s.status === "generating"} onClick={() => setTrimming(true)} className="rounded-full">
                <Scissors className="w-3.5 h-3.5 mr-1.5" />Trim
              </Button>
            )}
            {s?.duration && <span className="font-mono text-[10px] text-slate-500" data-testid="clip-duration">{s.duration.toFixed(1)}s</span>}
            <span className="ml-auto font-mono text-xs text-amber-400" data-testid="studio-approved-count">{approved} approved</span>
            <a data-testid="download-all-btn" href={approved ? `${API}/episodes/${ep.id}/download-all` : undefined}
              onClick={(e) => { if (!approved) { e.preventDefault(); toast.error("Approve at least one clip first"); } else toast("Preparing zip of approved clips…"); }}
              className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-amber-500 text-black text-sm font-medium hover:bg-amber-400 transition-colors">
              <Download className="w-4 h-4" />All approved
            </a>
          </div>
        </div>
        {s && <Controls s={s} prev={scenes[idx - 1]} ep={ep} chars={chars} reload={reload} />}
      </div>
      <FilmStrip scenes={scenes} activeId={activeId} onSelect={setActiveId} />
      {previewing ? (
        <EpisodePreview ep={ep} scenes={scenes} reload={reload} onClose={() => setPreviewing(false)}
          onOpenScene={(id) => { setPreviewing(false); setActiveId(id); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      ) : (
        <div className="panel p-4 flex flex-wrap items-center gap-3" data-testid="episode-preview-launcher">
          <Clapperboard className="w-4 h-4 text-amber-400" />
          <div className="text-sm">
            <div className="font-medium">Episode preview</div>
            <div className="text-[11px] text-slate-500">Watch the clips back-to-back, pause, trim and approve before building the episode file.</div>
          </div>
          <Button data-testid="open-episode-preview-btn" size="sm" disabled={!scenes.some((x) => x.video_path)} onClick={() => setPreviewing(true)} className="ml-auto rounded-full">
            <Play className="w-3.5 h-3.5 mr-1.5" />Preview episode
          </Button>
        </div>
      )}
      <FullEpisode ep={ep} approved={approved} reload={reload} />
    </div>
  );
};
